import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { Space, Tag, Typography } from 'antd';
import type { Company } from '@viq-admin/types';
import { crmSourceLabel, type CrmConnectionRow } from '@viq-admin/screens';
import { DataTable, space, type DataColumn } from '@viq-admin/ui';
import { crmFlagOn, enabledCrmLabels, managedByLabel } from './model';
import { useCompanyCrmConnections } from './useCompanyHubData';

/**
 * This company's CRM connections, each linking to its connection page
 * (`/integrations/:id`, where Test, Edit and Delete live under
 * `manage:Integration`). Read-only here.
 *
 * No "create" shortcut: the console's create route (`/integrations/new`)
 * cannot preselect a company, and HubSpot/Zoho connections are made by the
 * customer from their portal anyway. The empty state links to the
 * Integrations list instead.
 */

const stop = (e: MouseEvent) => e.stopPropagation();
const connectionHref = (id: string) => `/integrations/${id}`;

export function CompanyIntegrationsTab({ company }: { company: Company }) {
  const navigate = useNavigate();
  const { connections, isLoading, error, refetch } = useCompanyCrmConnections(company.id);
  const enabled = enabledCrmLabels(company);

  const columns: DataColumn<CrmConnectionRow>[] = [
    {
      key: 'crmSource',
      dataIndex: 'crmSource',
      title: 'CRM',
      kind: 'text',
      width: 160,
      projectValue: (r) => crmSourceLabel(r.crmSource),
      render: (_: unknown, r: CrmConnectionRow) => (
        <Link to={connectionHref(r.id)} onClick={stop}>
          {crmSourceLabel(r.crmSource)}
        </Link>
      ),
    },
    {
      key: 'managedBy',
      title: 'Connected via',
      kind: 'text',
      width: 200,
      projectValue: (r) => managedByLabel(r.managedBy),
      render: (_: unknown, r: CrmConnectionRow) => managedByLabel(r.managedBy),
    },
    {
      key: 'hasSecret',
      title: 'Credentials',
      kind: 'enum',
      width: 160,
      projectValue: (r) => (r.hasSecret ? 'Stored' : 'Missing'),
      render: (_: unknown, r: CrmConnectionRow) =>
        r.hasSecret ? <Tag color="green">Stored</Tag> : <Tag color="warning">Missing</Tag>,
    },
    {
      key: 'flag',
      title: 'Provider flag',
      kind: 'enum',
      width: 140,
      projectValue: (r) => (crmFlagOn(company, r.crmSource) ? 'On' : 'Off'),
      render: (_: unknown, r: CrmConnectionRow) =>
        crmFlagOn(company, r.crmSource) ? (
          <Tag color="green">On</Tag>
        ) : (
          <Tag color="warning" title="The company can't use this connection while the flag is off.">
            Off
          </Tag>
        ),
    },
    { key: 'updatedAt', dataIndex: 'updatedAt', title: 'Updated', kind: 'date' },
  ];

  return (
    <Space direction="vertical" size={space.md} style={{ width: '100%' }}>
      <Typography.Text>
        <Typography.Text type="secondary">Enabled by flag: </Typography.Text>
        {enabled.length > 0 ? enabled.join(', ') : 'none'}
      </Typography.Text>
      <DataTable<CrmConnectionRow>
        dataSource={connections}
        loading={isLoading}
        error={error ?? undefined}
        onRetry={() => void refetch()}
        columns={columns}
        rowKey="id"
        storageKey="companyHubIntegrations"
        onRowClick={(r) => navigate(connectionHref(r.id))}
        pagination={false}
        empty={{
          title: 'No CRM connection',
          description:
            'HubSpot and Zoho are connected by the company from their portal; Salesforce connections are added under Integrations (CRM).',
          action: <Link to="/integrations">Open Integrations (CRM)</Link>,
        }}
      />
    </Space>
  );
}
